/**
 * @file useHandleLogout.js
 * @module src/js/hooks/useHandleLogout
 * @description
 * **A hook to handle user logout by calling the backend API.**
 * - Utilizes React contexts to manage user state and modal messages.
 * - Returns a function that:
 *   - Sends a POST request to `/auth/logout` endpoint.
 *   - Clears user state upon successful logout.
 *   - Shows modal messages during the logout process.
 *   - Optionally navigates to the login page with appropriate state flags.
 *
 * @version
 * <p>
 *  2025-11-25: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Show modal messages during logout process. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Transform this from a pure JS function to a hook to utilize React contexts for user state and modal messages. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Avoid useFetchFromBackend to avoid maximum call stack error due to circular calls of useHandleLogout and useFetchFromBackend. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Use useRef to store navigate function to avoid unnecessary re-renders. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useCallback, useEffect, useRef } from "react";
import { useUser } from "../contexts/UserProvider";
import { useNavigate } from "react-router-dom";
import { useModal } from "../contexts/ModalProvider";

export default function useHandleLogout() {
    const navigate = useNavigate();
    const { setUid, setUgroup, setLastLogin, setIsLoggingOut } = useUser();
    const { showModalMessage } = useModal();
    const navigateRef = useRef(navigate);

    useEffect(() => {
        navigateRef.current = navigate;
    }, [navigate]);
    
    const handleLogout = useCallback(async ({
        sessionExpired = false,
        accountDeleted = false,
        from,
        scrollTo,
        showModal = true,
        navigateToLogin = true,
    } = {}) => {
        if (sessionExpired && accountDeleted) {
            throw new Error("Cannot have both sessionExpired and accountDeleted set to true.");
        }
        
        try {
            setIsLoggingOut(true);
            if (showModal) showModalMessage({ type: "info", content: "Logging out..." });
            
            const options = {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include"
            };

            const response = await fetch("/auth/logout", options);
            const data = await response.json();
            if (!data) throw new Error("No data received from backend");
            
            if (data.success) {
                setUid("Guest");
                setUgroup("Guest");
                setLastLogin("N/A");
                if (navigateToLogin) {
                    navigateRef.current("/login", {
                        replace: true, 
                        state:
                            sessionExpired ?
                                { expired: true, from, scrollTo } :
                            accountDeleted ?
                                { accountDeleted: true } : { byebye: true }
                    });
                }
            } else {
                console.error("Failed to log out");
                if (showModal) showModalMessage({ type: "error", content: 'Failed to log out. Please try again.' });
            }

        } catch (error) {
            console.error("An error occurred:", error);
            if (showModal) showModalMessage({ type: "error", content: 'An error occurred. Please try again.' });

        } finally {
            setIsLoggingOut(false);
        }
    }, [setIsLoggingOut, setUid, setUgroup, setLastLogin, showModalMessage]);
    // navigate changes on every path change due to the internal implementation of useNavigate().
    // This may cause unexpected re-renders.
    // To avoid this, we use a ref to always have the latest navigate function without causing re-renders.

    return handleLogout;
}